import { useQuery, useMutation } from '@apollo/client';
import { useMemo, useEffect } from 'react';
import { GET_USER_NOTIFICATIONS, GET_UNREAD_COUNT } from 'graphql/queries/userNotifications';
import {
    MARK_NOTIFICATION_AS_READ,
    MARK_ALL_NOTIFICATIONS_AS_READ,
    TOGGLE_NOTIFICATION_PIN,
    TOGGLE_NOTIFICATION_MUTE
} from 'graphql/mutations/userNotifications';
import { handleAddNotification } from 'layout/MainLayout/Header/FlexEndButtons/mocks/mockHandlers';

export const useUserNotifications = () => {
    // Queries
    const {
        data: notificationsData,
        loading: loadingNotifications,
        error: notificationsError,
        refetch: refetchNotifications
    } = useQuery(GET_USER_NOTIFICATIONS, {
        fetchPolicy: 'cache-and-network'
    });

    const { refetch: refetchUnreadCount } = useQuery(GET_UNREAD_COUNT, {
        fetchPolicy: 'cache-and-network'
    });

    // Mutations
    const [markAsReadMutation, { loading: markingAsRead }] = useMutation(MARK_NOTIFICATION_AS_READ, {
        refetchQueries: [{ query: GET_USER_NOTIFICATIONS }, { query: GET_UNREAD_COUNT }],
        awaitRefetchQueries: true
    });

    const [markAllAsReadMutation, { loading: markingAllAsRead }] = useMutation(MARK_ALL_NOTIFICATIONS_AS_READ, {
        refetchQueries: [{ query: GET_USER_NOTIFICATIONS }, { query: GET_UNREAD_COUNT }],
        awaitRefetchQueries: true
    });

    const [togglePinMutation, { loading: togglingPin }] = useMutation(TOGGLE_NOTIFICATION_PIN, {
        refetchQueries: [{ query: GET_USER_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    const [toggleMuteMutation, { loading: togglingMute }] = useMutation(TOGGLE_NOTIFICATION_MUTE, {
        refetchQueries: [{ query: GET_USER_NOTIFICATIONS }],
        awaitRefetchQueries: true
    });

    // Data transformation
    const notifications = useMemo(() => notificationsData?.getUserNotifications?.notifications || [], [notificationsData]);

    const unreadCount = useMemo(() => notificationsData?.getUserNotifications?.unreadCount || 0, [notificationsData]);

    // Wrapper functions
    const markNotificationAsRead = async (notificationId: number) => {
        try {
            await markAsReadMutation({
                variables: { notificationId }
            });
        } catch (error: any) {
            console.error('Error marking notification as read:', error);
            throw error;
        }
    };

    const markAllNotificationsAsRead = async () => {
        try {
            await markAllAsReadMutation();
        } catch (error: any) {
            console.error('Error marking all notifications as read:', error);
            throw error;
        }
    };

    const toggleNotificationPin = async (notificationId: number) => {
        try {
            await togglePinMutation({
                variables: { notificationId }
            });
        } catch (error: any) {
            console.error('Error toggling notification pin:', error);
            throw error;
        }
    };

    const toggleNotificationMute = async (notificationId: number) => {
        try {
            await toggleMuteMutation({
                variables: { notificationId }
            });
        } catch (error: any) {
            console.error('Error toggling notification mute:', error);
            throw error;
        }
    };

    // Simular notificaciones en tiempo real (equivalente al notificationService)
    useEffect(() => {
        // Solo activar si estamos en modo desarrollo con mocks
        const USE_REALTIME_SIMULATION = process.env.NODE_ENV === 'development';

        if (!USE_REALTIME_SIMULATION) {
            return;
        }

        // Generar notificaciones aleatorias en diferentes intervalos
        const interval5min = setInterval(() => {
            handleAddNotification();
            refetchNotifications();
        }, 5 * 60 * 1000); // 5 minutos

        const interval10min = setInterval(() => {
            handleAddNotification();
            refetchNotifications();
        }, 10 * 60 * 1000); // 10 minutos

        const interval30min = setInterval(() => {
            handleAddNotification();
            refetchNotifications();
        }, 30 * 60 * 1000); // 30 minutos

        // Cleanup
        // eslint-disable-next-line consistent-return
        return () => {
            clearInterval(interval5min);
            clearInterval(interval10min);
            clearInterval(interval30min);
        };
    }, [refetchNotifications]);

    return {
        notifications,
        unreadCount,
        loading: loadingNotifications,
        error: notificationsError,
        markingAsRead,
        markingAllAsRead,
        togglingPin,
        togglingMute,
        markNotificationAsRead,
        markAllNotificationsAsRead,
        toggleNotificationPin,
        toggleNotificationMute,
        refetchNotifications,
        refetchUnreadCount
    };
};
